import dns from "node:dns";

let installed = false;

/**
 * Route `*.services.ai.azure.com` lookups through `dns.resolve4` (c-ares), which follows the
 * multi-hop CNAME chain that the macOS stub resolver's `getaddrinfo` cannot. Idempotent.
 *
 * Without this, EVERY CLI call to the Foundry data plane fails with `ENOTFOUND` on macOS —
 * `curl` fails too, while `dns.resolve4` on the same host resolves fine. That made
 * `m8t platform email` unable to reconcile the executor at all from an operator's own
 * machine: it reported `executorUnreadable: "fetch failed"`, correctly refusing to claim
 * success, but with no way to proceed.
 *
 * Handles undici's `{all:true}` form (which must return an address ARRAY) and the two-arg
 * `(hostname, callback)` form, and falls back to the original lookup on any resolve failure
 * so a non-Foundry host is never affected.
 *
 * Mirrors `plugins/m8t/server/src/dns-shim.ts`, which fixed the same fault for the MCP
 * surface. Duplicated rather than shared because the plugin is a separate package that the
 * CLI does not depend on; keep the two in step.
 */
export function installFoundryDnsShim(): void {
  if (installed) return;
  installed = true;
  const origLookup = dns.lookup.bind(dns);
  // @ts-expect-error overriding the overloaded lookup signature
  dns.lookup = (hostname: string, options: unknown, cb: unknown) => {
    const opts = (typeof options === "function" ? {} : options) as { all?: boolean };
    const callback = (typeof options === "function" ? options : cb) as (
      err: NodeJS.ErrnoException | null, addr: unknown, fam?: number,
    ) => void;
    if (typeof hostname === "string" && hostname.endsWith("services.ai.azure.com")) {
      dns.resolve4(hostname, (err, addrs) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Node types `addrs` as string[], but on error the c-ares callback passes it undefined; the guard protects the .length access.
        if (err || !addrs?.length) { origLookup(hostname, options as never, cb as never); return; }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `opts` is cast from `unknown` options; a caller passing `lookup(host, undefined, cb)` leaves it undefined despite the cast asserting an object.
        if (opts?.all) callback(null, addrs.map((a) => ({ address: a, family: 4 })));
        else callback(null, addrs[0], 4);
      });
      return;
    }
    origLookup(hostname, options as never, cb as never);
  };
}
